import React from "react";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import SystemIcon from "../../systemIcon/SystemIcon";
import { useTranslation } from "@localization/useTranslation.ts";
import { useGeneralTabContent } from "./GeneralTabContent.hooks";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import { setFirstLetterToLowerCase } from "@common/stringOperations.ts";
import { SystemStatus } from "../../systemEdit/SystemEdit.types";
import SystemOverviewLegalEntityDialog from "../systemOverviewLegalEntityDialog/SystemOverviewLegalEntityDialog";
import { EntityType } from "@common/Common.types.ts";
import TopBarTextTile from "@common/components/entityDetailTopBar/TopBarTextTile";
import TopBarChipsTile from "@common/components/entityDetailTopBar/TopBarChipsTile";
import SystemStatusChip from "../SystemStatusChip";
import TopBarNumberTile from "@common/components/entityDetailTopBar/TopBarNumberTile";
import TopBarNoteTile from "@common/components/entityDetailTopBar/TopBarNoteTile";
import TopBarTile, { useTopBarTileSize } from "@common/components/entityDetailTopBar/TopBarTile";
import OverviewTileRows from "@common/components/entityDetailTopBar/OverviewTileRows";
import { getOverviewFieldTiles } from "@common/components/entityDetailTopBar/overviewFieldTiles";
import { SystemStandardField } from "../../../entityTemplates/EntityTemplate.types";
import RiskIndicator from "../../../riskAssessment/riskIndicator/RiskIndicator";
import RiskAreaRisksTile from "../../../risk/riskAreaRisksTile/RiskAreaRisksTile";
import { RiskAssessmentType } from "../../../riskAssessment/RiskAssessment.types";
import { RiskAssessmentArea, RiskComplianceArea } from "../../../riskAssessment/riskAssessments/RiskAssessments.types";
import { useUrlProvider } from "../../../useUrlProvider";
import { DataProcessorViewModel } from "../SystemOverview.types";

export interface GeneralTabContentProps {
    systemId?: string;
    systemName: string;
}

function GeneralTabContent(props: GeneralTabContentProps) {
    const { getRiskAssessmentQuestionsDialog, getLegalEntityUrl, getGroupEntityUrl } = useUrlProvider();
    const { translateString } = useTranslation();
    const {
        isLoading,
        infoData,
        infoRefetch,
        showSubSuppliers,
        setShowSubSuppliers,
        permissions,
        hasEnterpriseRiskManagementAccess,
        showSuppliers,
        setShowSuppliers,
    } = useGeneralTabContent(props);
    const { platformFeatures, addonsFeatures } = usePlanContext();
    const topBarTileSize = useTopBarTileSize();

    const numberOfSuppliers = infoData?.suppliers?.length ?? 0;
    const numberOfSubProcessors = infoData?.subProcessors?.length ?? 0;

    const getLegalEntityLink = (entity: DataProcessorViewModel) => (entity.isGroupEntity ? getGroupEntityUrl(entity.id) : getLegalEntityUrl(entity.id));

    const getSuppliersTile = () =>
        numberOfSuppliers === 1 ? (
            <TopBarTextTile
                label={translateString("suppliers")}
                value={infoData!.suppliers[0].name}
                linkTo={getLegalEntityLink(infoData!.suppliers[0])}
                isLoading={isLoading}
            />
        ) : (
            <TopBarNumberTile
                label={translateString("suppliers")}
                value={numberOfSuppliers === 0 ? undefined : numberOfSuppliers}
                onClick={numberOfSuppliers > 1 ? () => setShowSuppliers(true) : undefined}
                isLoading={isLoading}
            />
        );

    const getSubSuppliersTile = () =>
        numberOfSubProcessors === 1 ? (
            <TopBarTextTile
                label={translateString("secondarySuppliers")}
                value={infoData!.subProcessors[0].name}
                linkTo={getLegalEntityLink(infoData!.subProcessors[0])}
                isLoading={isLoading}
            />
        ) : (
            <TopBarNumberTile
                label={translateString("secondarySuppliers")}
                value={numberOfSubProcessors === 0 ? undefined : numberOfSubProcessors}
                onClick={numberOfSubProcessors > 1 ? () => setShowSubSuppliers(true) : undefined}
                isLoading={isLoading}
            />
        );

    const topRowTiles = getOverviewFieldTiles(infoData?.overviewTopFields, {
        [SystemStandardField.SystemBusinessAreas]: {
            key: "businessAreas",
            render: () => <TopBarChipsTile label={translateString("businessAreas")} chips={infoData?.businessAreas} isLoading={isLoading} />,
        },
        [SystemStandardField.SystemResponsible]: {
            key: "responsible",
            render: () => <TopBarTextTile label={translateString("responsible")} value={infoData?.responsible} isLoading={isLoading} />,
        },
        // Secondary suppliers has no standard field of its own, so it follows the suppliers field.
        [SystemStandardField.SystemSuppliers]: [
            { key: "suppliers", render: getSuppliersTile },
            { key: "secondarySuppliers", render: getSubSuppliersTile },
        ],
    });

    const bottomRowTiles = getOverviewFieldTiles(infoData?.overviewBottomFields, {
        [SystemStandardField.SystemStatus]: {
            key: "status",
            render: () => (
                <TopBarTextTile
                    label={translateString("status")}
                    neverShowTooltip
                    value={
                        infoData?.status ? (
                            <SystemStatusChip
                                status={infoData.status}
                                value={translateString(setFirstLetterToLowerCase(SystemStatus[infoData.status].toString()))}
                            />
                        ) : undefined
                    }
                    isLoading={isLoading}
                />
            ),
        },
        [SystemStandardField.SystemTags]: {
            key: "tags",
            render: () =>
                permissions.canAccessTags &&
                platformFeatures.tags && <TopBarChipsTile label={translateString("tags")} chips={infoData?.tags} isLoading={isLoading} />,
        },
    });

    const noteTile = (
        <TopBarNoteTile
            note={infoData?.note}
            onNoteUpdated={() => infoRefetch()}
            postUrl={`/System/${infoData?.id}/note`}
            entityId={infoData?.id}
            entityType={EntityType.System}
            isLoading={isLoading}
        />
    );

    const riskTile = hasEnterpriseRiskManagementAccess ? (
        <RiskAreaRisksTile entityType={EntityType.System} entityId={props.systemId!} />
    ) : (
        permissions.riskPermissions.read &&
        !addonsFeatures.research && (
            <TopBarTile label={translateString("riskLevel")} isLoading={isLoading}>
                <Box
                    sx={(theme) => ({
                        color: theme.palette.primary.dark,
                        fontSize: theme.typography.pxToRem(12),
                        display: "flex",
                        justifyContent: "center",
                        boxOrient: "vertical",
                        overflow: "hidden",
                    })}
                >
                    <RiskIndicator
                        riskAssessmentType={RiskAssessmentType.Risk}
                        fixedWidthLeftColumn
                        textSize="sm"
                        score={infoData?.riskAssessmentTotalScore}
                        linkHref={getRiskAssessmentQuestionsDialog(
                            props.systemId ?? "",
                            RiskAssessmentArea.Systems,
                            RiskComplianceArea.NIS2,
                            infoData?.currentRiskAssessmentVersionId ? infoData.currentRiskAssessmentVersionId : "null"
                        )}
                    />
                </Box>
            </TopBarTile>
        )
    );

    return (
        <>
            <OverviewTileRows
                isLoading={isLoading}
                tiles={[
                    <React.Fragment key="icon">
                        <Grid size={topBarTileSize}>
                            <SystemIcon systemName={props.systemName} size={"medium"} />
                        </Grid>
                    </React.Fragment>,
                    <React.Fragment key="template">
                        <TopBarTextTile label={translateString("template")} value={infoData?.templateName} isLoading={isLoading} />
                    </React.Fragment>,
                    ...topRowTiles,
                    ...bottomRowTiles,
                    <React.Fragment key="note">{noteTile}</React.Fragment>,
                    riskTile && <React.Fragment key="risk">{riskTile}</React.Fragment>,
                ]}
            />

            {showSubSuppliers && (
                <SystemOverviewLegalEntityDialog
                    onDialogClose={() => setShowSubSuppliers(false)}
                    legalEntities={infoData?.subProcessors}
                    header={translateString("secondarySuppliers")}
                />
            )}

            {showSuppliers && (
                <SystemOverviewLegalEntityDialog
                    onDialogClose={() => setShowSuppliers(false)}
                    legalEntities={infoData?.suppliers}
                    header={translateString("suppliers")}
                />
            )}
        </>
    );
}

export default GeneralTabContent;
